import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/devLog";

const AuthCtx = createContext(null);

/**
 * Auth state is mirrored in React only — never in localStorage / sessionStorage.
 *
 * The single source of truth is the HttpOnly `bakery_session` cookie which is
 * inaccessible to JavaScript. On mount we always re-validate via /auth/me;
 * during that round-trip the UI shows a brief loading state instead of any
 * cached identity, so XSS payloads can't read user data out of storage.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always re-verify against the server — the HttpOnly cookie IS the session.
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
    // Mount-only session validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (pin, otpCode = null) => {
    // Backend sets HttpOnly session + CSRF cookies; we discard the bearer
    // token returned in the body so it never touches storage. `otpCode` is
    // only sent when the admin is verifying a new device via email-OTP.
    const body = otpCode ? { pin, otp_code: otpCode } : { pin };
    const { data } = await api.post("/auth/login", body);
    setUser(data.user);
    return data.user;
  }, []);

  const requestOtp = useCallback(async (pin) => {
    const { data } = await api.post("/auth/otp/request", { pin });
    return data; // { ok, sent_to, expires_in }
  }, []);

  const requestPinReset = useCallback(async () => {
    const { data } = await api.post("/auth/pin-reset/request", {});
    return data; // { ok, sent_to, expires_in }
  }, []);

  const verifyPinReset = useCallback(async (code, new_pin) => {
    const { data } = await api.post("/auth/pin-reset/verify", { code, new_pin });
    return data;
  }, []);

  /**
   * True when the current user has the named permission key.
   * - Admins always pass (role implies every flag).
   * - During load (`user` still null) returns false so guarded UI hides cleanly.
   */
  const hasPermission = useCallback(
    (key) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      return Array.isArray(user.permissions) && user.permissions.includes(key);
    },
    [user]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Best-effort — even if the call fails (offline, network hiccup) we
      // still want the React state cleared so the user lands on the PIN
      // screen. Log so this never silently disappears on a real bug.
      devLog("Logout API call failed (clearing local state anyway):", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      loading,
      requestOtp,
      requestPinReset,
      verifyPinReset,
      hasPermission,
    }),
    [user, login, logout, loading, requestOtp, requestPinReset, verifyPinReset, hasPermission]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
